<template>
  <LoadingPage v-if="isLoading"></LoadingPage>
  
  <div class="client-questions-card full-page-view">
    <div class="card-header">
      <div class="header-left">
        <h2 class="card-title">Client Questions</h2>
        <div class="search-wrapper">
          <input 
            v-model.trim="search" 
            type="text" 
            class="form-control search-input"
            placeholder="Search questions..."
            @keyup="onSearch" 
          />
        </div>
      </div>

      <div class="controls">
        <div class="control-group">
          <label for="sort-by">Sort By:</label>
          <select id="sort-by" v-model="sortBy" class="form-select" @change="init">
            <option v-for="sortOption in sortByList" :value="sortOption.value" :key="sortOption.value">
              {{ sortOption.text }}
            </option>
          </select>
        </div>
        <div class="control-group">
          <label for="rows-per-page">Rows:</label>
          <select id="rows-per-page" v-model="pagination.pageSize" class="form-select" @change="init">
            <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}</option>
          </select>
        </div>

        <div class="pagination">
          <button @click="previousPage" :disabled="pagination.page === 1" class="pagination-arrow">
            <i class="fas fa-chevron-left"></i>
          </button>
          <span class="pagination-label">
            <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
          </span>
          <button @click="nextPage" :disabled="pagination.page === pagination.totalPages" class="pagination-arrow">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <div class="questions-list">
      <div v-if="!questionsList.length && !isLoading" class="empty-state">No records to display.</div>
      
      <div 
        v-else 
        v-for="question in questionsList" 
        :key="question.id" 
        class="question-row clickable-row" 
        @click="viewQuestion(question)"
      >
        <div class="question-info-wrapper">
          <div class="question-date">
            <div class="date-formatted">{{ formatDateDisplay(question.created_at) }}</div>
          </div>

          <div class="question-info">
            <div class="question-lead">
              <span class="org-name">{{ question.proposal.Lead.auth_organisation_name || question.proposal.Lead.organisation_name }}</span>
              <span class="status-badge" :style="{ backgroundColor: getStatusColor(question.status) }">
                {{ getQuestionStatusText(question.status) }}
              </span>
            </div>
            
            <div class="question-meta">
              <span class="sub-label-gray">
                {{ question.question_by_email || question.proposal.Lead.email }} 
                <strong>({{ question.question_by_email_role || 'Main Lead' }})</strong>
              </span>
              <i class="fas fa-copy copy-icon" @click.stop="copyEmail(question.question_by_email || question.proposal.Lead.email)" title="Copy Email"></i>
            </div>

            <div class="question-proposal-title">Proposal: {{ question.proposal.proposal_title }}</div>
            <div class="question-text" :title="question.question">
              {{ truncateText(question.question, 140) }}
            </div>
          </div>
        </div>

        <div class="question-action">
          <button type="button" class="btn-svg-icon position-relative" title="View Question" @click.stop="viewQuestion(question)">
              <img src="@/assets/img/icons/preview.svg" alt="View" class="icon-svg" />
              <span v-if="!question.is_opened" class="exclamation-icon">
                <i class="fa-solid fa-circle-exclamation"></i>
              </span>
          </button>
          
          <button type="button" class="btn-svg-icon" title="Mark as Resolved" 
            @click.stop="resolveQuestion(question.id)" :disabled="!canManageQuestions(question) || question.status === 1">
            <img src="@/assets/img/icons/Save.svg" alt="Resolve" class="icon-svg" />
          </button>
          
          <button type="button" :disabled="!canManageQuestions(question)" class="btn-svg-icon" title="Send Reply" @click.stop="sendReply(question)">
            <img src="@/assets/img/icons/send.svg" alt="Send Reply" class="icon-svg" />
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-if="isModalOpen" class="modal-backdrop-custom" @click="closeModalOnOutsideClick">
      <div class="modal-content-custom" @click.stop>
          <button @click="isModalOpen = false" class="close-button-custom">&times;</button>
          <h5 class="modal-title-custom">Question Details</h5>
          <hr>
          <div class="modal-body" v-if="currentQuestion">
              <p><strong>Organisation:</strong> {{ currentQuestion.proposal.Lead.auth_organisation_name || currentQuestion.proposal.Lead.organisation_name }}</p>
              <p>
                <strong>Questioned By:</strong> {{ currentQuestion.question_by_email || currentQuestion.proposal.Lead.email }}
                <i class="fas fa-copy copy-icon" @click.stop="copyEmail(currentQuestion.question_by_email || currentQuestion.proposal.Lead.email)"></i>
              </p>
              <p><strong>Role:</strong> {{ currentQuestion.question_by_email_role || 'Main Lead' }}</p>
              <p><strong>Proposal:</strong> {{ currentQuestion.proposal.proposal_title }}</p>
              <hr>
              <p><strong>Question:</strong></p>
              <div class="full-question-box">{{ currentQuestion.question }}</div>
          </div>
      </div>
  </div>

  <ConfirmModal
     v-model="showConfirmModal"
     :title="confirmModalTitle"
     :message="confirmModalMessage"
     @confirm="onConfirmAction"
  />
</template>

<script>
import { debounce, truncateLongText } from '@/plugins/helper.js';
import LoadingPage from '@/components/Utilities/vertex-loading.vue';
import axios from 'axios';

export default {
    name: 'FullQuestionsPage',
    components: { LoadingPage },
    data() {
        return {
            isLoading: false,
            search: "",
            questionsList: [],
            pagination: { pageSize: 10, page: 1, totalItems: 0, totalPages: 1 },
            rowsPerPageList: [{ text: "10", value: 10 }, { text: "20", value: 20 }, { text: "50", value: 50 }],
            sortByList: [
                { text: "Newest First", value: "created_at" },
                { text: "Oldest First", value: "created_at_asc" },
            ],
            sortBy: "created_at",
            debounceFn: null,
            isModalOpen: false,
            showConfirmModal: false,
            confirmModalTitle: '',
            confirmModalMessage: '',
            pendingActionId: null,
            currentQuestion: null,
            colorForStatus: { pending: '#F3994D', resolved: '#00A54F' },
        };
    },
    computed: {
        paginationLabel() {
            const start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems);
            const end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems);
            return this.pagination.totalItems === 0 ? '0' : `${start} - ${end}`;
        },
    },
    created() {
        this.init();
        this.debounceFn = debounce(() => this.init(), 500);
    },
    methods: {
        async init() {
            this.isLoading = true;
            try {
                const sortOrder = this.sortBy === 'created_at' ? 'desc' : 'asc';
                const response = await axios.get('/questions/get-all', {
                    params: {
                        page: this.pagination.page,
                        pageSize: this.pagination.pageSize,
                        sortBy: 'created_at',
                        sortOrder: sortOrder,
                        search: this.search,
                    }
                });
                if (response.data.success) {
                    this.questionsList = response.data.questions || [];
                    this.pagination.totalItems = response.data.totalItems;
                    this.pagination.totalPages = Math.ceil(this.pagination.totalItems / this.pagination.pageSize);
                }
            } catch (error) {
                console.error("Failed to fetch questions:", error);
            } finally {
                this.isLoading = false;
            }
        },
        onSearch() {
            this.pagination.page = 1;
            this.debounceFn();
        },
        previousPage() { if (this.pagination.page > 1) this.pagination.page--; },
        nextPage() { if (this.pagination.page < this.pagination.totalPages) this.pagination.page++; },
        formatDateDisplay(timestamp) {
            if (!timestamp) return 'N/A';
            return new Date(timestamp).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        },
        getQuestionStatusText(status) { return status === 0 ? 'Pending' : 'Resolved'; },
        getStatusColor(status) { return status === 0 ? this.colorForStatus.pending : this.colorForStatus.resolved; },
        truncateText(text, length) { return truncateLongText(text, length); },
        viewQuestion(question) {
            this.currentQuestion = question;
            this.isModalOpen = true;
            if (!question.is_opened) this.markQuestionAsOpened(question.id);
        },
        async markQuestionAsOpened(questionId) {
            try {
                const response = await axios.put(`/questions/${questionId}/mark-opened`);
                if (response.data.success) {
                    const q = this.questionsList.find(q => q.id === questionId);
                    if (q) q.is_opened = true;
                }
            } catch (error) { console.error("Error marking as opened:", error); }
        },
        resolveQuestion(questionId) {
            this.pendingActionId = questionId;
            this.confirmModalTitle = 'Resolve Question';
            this.confirmModalMessage = 'Are you sure you want to mark this question as resolved?';
            this.showConfirmModal = true;
        },
        async onConfirmAction() {
            const questionId = this.pendingActionId;
            this.pendingActionId = null;
            if (!questionId) return;
            try {
                const response = await axios.put(`/questions/${questionId}/resolve`);
                if (response.data.success) {
                    this.$toast.success("Question marked as resolved.");
                    this.init(); 
                }
            } catch (error) { this.$toast.error("An error occurred while resolving."); }
        },
        closeModalOnOutsideClick(e) { if (e.target.classList.contains('modal-backdrop-custom')) this.isModalOpen = false; },
        async copyEmail(email) {
            try {
                await navigator.clipboard.writeText(email);
                this.$toast.success("Email copied to clipboard!");
            } catch (err) { this.$toast.error("Failed to copy email."); }
        },
        canManageQuestions(question) {
            if (this.$store.state.auth.isAdmin) {
                return false;
            }
            if (this.$store.getters['auth/isGlobalSuperAdmin']) {
                return true;
            }
            if (this.$store.state.auth.isConsultant) {
                const currentUserId = this.$store.state.auth.currentUser?.id || 0;
                const creatorId = question.proposal?.Lead?.created_by || question.proposal?.created_by || 0;
                return creatorId > 0 && currentUserId === creatorId;
            }
            return false;
        },
        sendReply(question) {
            if (!this.canManageQuestions(question)) return;
            const email = question.question_by_email || question.proposal?.Lead?.email;
            if (email) {
                window.location.href = `mailto:${email}?subject=RE: ${encodeURIComponent(question.proposal?.proposal_title || '')}`;
            }
        }
    },
    watch: {
        'pagination.page'() { this.init(); }
    }
};
</script>

<style scoped>
/* Main Layout Style */
.client-questions-card {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  font-family: 'Roboto', sans-serif;
  margin: 1.5rem;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
  flex-wrap: wrap;
  gap: 15px;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 20px;
    flex-grow: 1;
}

.card-title { font-size: 1.4rem; font-weight: 600; margin: 0; color: #333; }

.search-input {
    max-width: 300px;
    border-radius: 8px;
    height: 40px;
    border: 1px solid #dee2e6;
    padding-left: 12px;
}

/* Controls & Pagination */
.controls { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
.control-group { display: flex; align-items: center; gap: 8px; }
.control-group label { color: #6c757d; font-size: 0.9rem; font-weight: 500; white-space: nowrap; }
.form-select { border-radius: 6px; border: 1px solid #dee2e6; padding: 6px 30px 6px 12px; cursor: pointer; }

.pagination { display: inline-flex; align-items: center; border: 1px solid #dee2e6; border-radius: 8px; background-color: #fff; }
.pagination-label { font-size: 0.9rem; padding: 0 12px; color: #435666; }
.pagination-arrow { background: transparent; border: none; cursor: pointer; color: #00A0C1; padding: 8px 12px; transition: background 0.2s; }
.pagination-arrow:hover:not(:disabled) { background-color: #f8f9fa; }
.pagination-arrow:disabled { color: #ced4da; cursor: not-allowed; }

/* List Item Styles */
.questions-list { padding: 24px; display: flex; flex-direction: column; gap: 15px; }

.question-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #fff;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;
  overflow: hidden;
}

.clickable-row { cursor: pointer; }
.clickable-row:hover { 
    border-color: #00A0C1; 
    box-shadow: 0 4px 10px rgba(0, 160, 193, 0.1);
    background-color: #fcfdfe;
}

.question-info-wrapper { display: flex; align-items: stretch; flex-grow: 1; }

.question-date {
  background-color: #435666;
  color: #fff;
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 15px;
  text-align: center;
}
.date-formatted { font-weight: 700; font-size: 0.85rem; text-transform: uppercase; }

.question-info { flex-grow: 1; padding: 15px 25px; display: flex; flex-direction: column; gap: 4px; }
.org-name { font-weight: 700; font-size: 1.1rem; color: #333; margin-right: 10px; }

.status-badge {
    font-size: 0.7rem;
    padding: 2px 10px;
    border-radius: 5px;
    color: white;
    text-transform: uppercase;
    font-weight: bold;
}

.question-meta { display: flex; align-items: center; gap: 8px; margin-bottom: 2px; }
.sub-label-gray { font-size: 0.85rem; color: #6c757d; }
.question-proposal-title { font-size: 0.95rem; font-weight: 600; color: #00A0C1; }
.question-text { color: #555; font-size: 0.95rem; line-height: 1.4; margin-top: 6px; }

.question-action { display: flex; align-items: center; gap: 12px; padding-right: 25px; }

/* --- ICON & BUTTON STYLES: EXACT MATCH TO CLIENTQUESTIONS.VUE --- */
.btn-svg-icon {
   width: 30px; /* Matched to ClientQuestions.vue */
   height: 30px; /* Matched to ClientQuestions.vue */
   border-radius: 50%;
   display: flex;
   align-items: center;
   justify-content: center;
   background-color: transparent;
   border: none;
   cursor: pointer;
   transition: background-color 0.2s ease;
}

.btn-svg-icon:hover:not(:disabled) {
   background-color: #00A0C1; /* Matched */
}

.btn-svg-icon:hover:not(:disabled) .icon-svg {
   filter: brightness(0) invert(1); /* Matched */
}

.btn-svg-icon:disabled { 
    opacity: 0.3; 
    cursor: not-allowed; 
    pointer-events: none;
}

.icon-svg {
   width: 30px; /* Matched to ClientQuestions.vue */
   height: 30px; /* Matched to ClientQuestions.vue */
}

.exclamation-icon {
    position: absolute;
    top: -5px; /* Matched */
    right: -5px; /* Matched */
    color: #F3994D; 
    font-size: 1.1em;
    background-color: white;
    border-radius: 50%;
    line-height: 1;
}

.copy-icon { cursor: pointer; color: #adb5bd; font-size: 0.85rem; padding: 5px; transition: 0.2s; }
.copy-icon:hover { color: #00A0C1; background-color: #e9ecef; border-radius: 4px; }

/* Modal & Utilities */
.modal-backdrop-custom {
    position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex; justify-content: center; align-items: center; z-index: 1050;
}
.modal-content-custom {
    position: relative; background-color: white; padding: 2.5rem 2rem;
    border-radius: 16px; width: 100%; max-width: 550px;
    box-shadow: 0 10px 25px rgba(0,0,0,0.2);
}
.modal-title-custom { font-weight: 700; font-size: 1.3rem; }
.full-question-box {
    background-color: #f8f9fa; padding: 18px; border-radius: 12px;
    border: 1px solid #dee2e6; white-space: pre-wrap;
    font-size: 1rem; color: #435666; line-height: 1.6;
}
.close-button-custom {
    position: absolute; top: 1.2rem; right: 1.2rem;
    font-size: 1.8rem; border: none; background: none; cursor: pointer; color: #999;
}
.close-button-custom:hover { color: #333; }
.empty-state { text-align: center; padding: 40px; color: #6c757d; font-style: italic; }
</style>